import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const MilestoneBurnupChart = ({ milestone, executions, burnupData }) => {
  const data = useMemo(() => {
    if (!milestone?.start_date || !milestone?.due_date) return [];

    const start = new Date(milestone.start_date);
    const end = new Date(milestone.due_date);
    const today = new Date();
    
    // Normalize times to start of day
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const dayLength = 1000 * 60 * 60 * 24;
    const totalDays = (end - start) / dayLength;
    if (totalDays <= 0) return [];

    // Decision: Use burnupData (test-based) if available and valid, otherwise fallback to executions (legacy run-based)
    // User requested granular progress based on passed tests ("concluído nos cenários")
    // Fix: Only use test-based if there is actual history data. If tests exist but none are passed (history empty),
    // and the run is marked completed, we should fall back to legacy execution-based progress to show completion.
    const useTestBased = !!burnupData && burnupData.total > 0 && Array.isArray(burnupData.history) && burnupData.history.length > 0;
    const totalScope = useTestBased ? burnupData.total : executions.length;
    
    // Legacy: Sort completed executions by date
    const completedExecutions = !useTestBased ? executions
      .filter(e => e.status === 'completed' && e.completed_at)
      .map(e => ({ ...e, date: new Date(e.completed_at) }))
      .sort((a, b) => a.date - b.date) : [];

    // New: Prepare cumulative map for passed tests
    const passedMap = new Map();
    if (useTestBased) {
        let cumulative = 0;
        // Ensure history is sorted by date
        const sortedHistory = [...burnupData.history].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        sortedHistory.forEach(h => {
            // Parse YYYY-MM-DD to local date to match loop
            const [y, m, d] = h.date.split('-').map(Number);
            const dateObj = new Date(y, m - 1, d);
            dateObj.setHours(0,0,0,0);
            
            cumulative += h.count;
            // Store cumulative count for this timestamp
            // Note: If multiple history entries fall on same local date (unlikely with YYYY-MM-DD grouping), 
            // this logic sums them up implicitly if we sum h.count properly? 
            // Wait, history is grouped by date.
            passedMap.set(dateObj.getTime(), cumulative);
        });
    }

    const chartData = [];
    // Start chart 1 day before the official start date for better visibility
    let currentDate = new Date(start);
    currentDate.setDate(currentDate.getDate() - 1);

    const statusRaw = String(milestone?.status || '').trim().toLowerCase();
    const completedLikeStatuses = new Set([
      'completed',
      'concluido',
      'concluida',
      'concluído',
      'concluída',
      'finalizado',
      'finalizada',
      'finished',
      'passed'
    ]);

    let rangeEnd = new Date(today > end ? today : end);
    if (completedLikeStatuses.has(statusRaw)) {
      const completedAtRaw = milestone?.completed_at || milestone?.completedAt || milestone?.updated_at;
      const completedAt = completedAtRaw ? new Date(completedAtRaw) : null;
      if (completedAt && !Number.isNaN(completedAt.getTime())) {
        completedAt.setHours(0, 0, 0, 0);
        if (completedAt.getTime() >= start.getTime()) {
          rangeEnd = completedAt;
        }
      }
    }

    const finalDate = new Date(rangeEnd);
    finalDate.setDate(finalDate.getDate() + 1);

    let currentPassed = 0;

    while (currentDate <= finalDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const currentTime = currentDate.getTime();
      
      // Calculate Planned
      const daysPassed = (currentDate - start) / dayLength;
      let planned = (daysPassed / totalDays) * 100;
      planned = Math.max(0, Math.min(100, planned)); // Clamp between 0 and 100

      // Calculate Real
      let real = null;
      if (currentDate <= today || (currentDate.getTime() === today.getTime())) {
        if (totalScope === 0) {
          real = 0;
        } else {
            if (useTestBased) {
                // Update currentPassed if we have data for this day
                // We need to look for the cumulative value up to this day.
                // Since passedMap only contains dates where changes happened,
                // we check if the current date is in the map.
                if (passedMap.has(currentTime)) {
                    currentPassed = passedMap.get(currentTime);
                }
                // If not in map, currentPassed remains the last known value (cumulative)
                
                // Edge case: If history starts AFTER start date, currentPassed starts at 0. Correct.
                // Edge case: If history has gaps, currentPassed holds steady. Correct.
                
                real = (currentPassed / totalScope) * 100;
            } else {
                // Legacy logic
                const endOfDay = new Date(currentDate);
                endOfDay.setHours(23, 59, 59, 999);
                const completedCount = completedExecutions.filter(e => e.date <= endOfDay).length;
                real = (completedCount / totalScope) * 100;
            }
        }
      }

      chartData.push({
        date: dateStr,
        displayDate: currentDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        planned: parseFloat(planned.toFixed(1)),
        real: real !== null ? parseFloat(real.toFixed(1)) : null
      });

      // Next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return chartData;
  }, [milestone, executions, burnupData]);

  if (!data.length) return null;

  // Paleta clara — este gráfico também é usado dentro de um card branco
  // (aba Planos de Teste); antes só existia no painel escuro da Milestones
  // antiga, por isso as cores originais (texto claro, fundo #333) ficavam
  // ilegíveis fora daquele contexto.
  return (
    <div style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="displayDate"
            stroke="#9CA3AF"
            tick={{ fill: '#6B7280', fontSize: 11 }}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke="#9CA3AF"
            tick={{ fill: '#6B7280', fontSize: 11 }}
            domain={[0, 100]}
            unit="%"
            width={40}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
            itemStyle={{ color: '#1F2937' }}
            labelStyle={{ color: '#6B7280' }}
            formatter={(value, name) => [`${value}%`, name === 'planned' ? 'Planejado' : 'Real']}
          />
          <Legend wrapperStyle={{ fontSize: 12, color: '#4B5563' }} />
          <Line
            type="monotone"
            dataKey="planned"
            stroke="#9CA3AF"
            name="Planejado"
            strokeWidth={2}
            dot={false}
            strokeDasharray="5 5"
          />
          <Line
            type="monotone"
            dataKey="real"
            stroke="#2F6FE4"
            name="Real"
            strokeWidth={2.5}
            dot={{ r: 3, fill: '#2F6FE4', strokeWidth: 0 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MilestoneBurnupChart;
